import React, { useState } from 'react';
import { InlineSpinner } from '../common/loading';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  Divider,
IconButton,
  Link,
  alpha,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon,
  Launch as LaunchIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { SEARCE_COLORS } from '../../theme';
import { CustomerRequestResponse, customerRequestApi } from '../../services/api';
import { getEscalationResolutionStatusLabel } from '../../utils/customerSentimentutils';
import { FileViewer } from '../modules/FileViewer';
import { normalizeZohoIssueLinkUrl, zohoIssueLinkProps } from './zohoIssueUrl';

interface CustomerRequestDetailModalProps {
  open: boolean;
  onClose: () => void;
  request: CustomerRequestResponse | null;
  loading?: boolean;
}

// Helper function to format dates in Searce standard format (DD-MMM-YY)
const formatDateEnhanced = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = String(date.getFullYear()).slice(-2);
    return `${day}-${month}-${year}`;
  } catch {
    return '-';
  }
};

// Helper function to format full date with time (Mon DD, YYYY)
const formatFullDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    
    return date.toLocaleString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return '-';
  }
};

// DD-Mon-YYYY (with time) for response timestamp
const formatDateDDMonYYYY = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    const time = date.toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    return `${day}-${month}-${year}, ${time}`;
  } catch {
    return '-';
  }
};

// Status chip component - matches table colors exactly
const StatusChip: React.FC<{ status: string; type?: 'status' | 'type' | 'level' }> = ({ status, type = 'status' }) => {
  const getChipColor = () => {
    const statusLower = status.toLowerCase();
    
    // Type/Level styles (matches table)
    if (type === 'level' || type === 'type') {
      if (statusLower === 'project') {
        return { bg: alpha(SEARCE_COLORS.primary, 0.15), color: SEARCE_COLORS.primary };
      }
      if (statusLower === 'individual') {
        return { bg: alpha(SEARCE_COLORS.secondary, 0.15), color: SEARCE_COLORS.secondary };
      }
      if (statusLower === 'neutral') {
        return { bg: alpha(SEARCE_COLORS.gray_400, 0.2), color: SEARCE_COLORS.textSecondary };
      }
    }
    
    // Status styles (matches table exactly)
    if (statusLower.includes('positive') || statusLower.includes('resolved') || statusLower.includes('acknowledged') || statusLower.includes('responded')) {
      return { bg: alpha(SEARCE_COLORS.success, 0.15), color: SEARCE_COLORS.success };
    }
    if (statusLower.includes('negative') || statusLower.includes('open') || statusLower.includes('failed')) {
      return { bg: alpha(SEARCE_COLORS.error, 0.15), color: SEARCE_COLORS.error };
    }
    if (statusLower.includes('pending') || statusLower.includes('awaiting')) {
      return { bg: alpha(SEARCE_COLORS.warning, 0.15), color: SEARCE_COLORS.warning };
    }
    if (statusLower.includes('sent')) {
      return { bg: alpha(SEARCE_COLORS.primary, 0.15), color: SEARCE_COLORS.primary };
    }
    if (statusLower.includes('closed')) {
      return { bg: alpha(SEARCE_COLORS.gray_400, 0.2), color: SEARCE_COLORS.textSecondary };
    }
    
    // Default
    return { bg: alpha(SEARCE_COLORS.primary, 0.1), color: SEARCE_COLORS.primary };
  };

  const { bg, color } = getChipColor();

  return (
    <Chip
      label={status}
      size="small"
      sx={{
        backgroundColor: bg,
        color: color,
        fontWeight: 500,
        fontSize: '0.75rem',
        height: 24,
        borderRadius: '12px',
        '& .MuiChip-label': { px: 1.5 },
      }}
    />
  );
};

// Helper function to get sentiment from overall_satisfaction
const getSentiment = (satisfaction: string | null | undefined): string => {
  if (!satisfaction) return '-';
  const sat = satisfaction.toLowerCase();
  if (sat.includes('highly dissatisfied') || sat.includes('dissatisfied')) {
    return 'Negative';
  } else if (sat.includes('highly satisfied') || sat.includes('satisfied')) {
    return 'Positive';
  } else if (sat === 'neutral') {
    return 'Neutral';
  }
  return '-';
};

// Compact info display - for grid layouts (always renders, even if empty)
// Uses neutral backgrounds, but primary color for icons to add visual interest
const CompactInfo: React.FC<{ icon: React.ReactNode; label: string; value: string | null | undefined; color?: string }> = ({ 
  icon, 
  label, 
  value, 
  color 
}) => {
  // Use neutral gray for backgrounds/borders, but allow color override for semantic meaning
  const neutralColor = SEARCE_COLORS.gray_400 || '#9CA3AF';
  const bgColor = color || neutralColor;
  // Icons use primary color for visual interest, unless a specific color is provided
  const iconColor = color || SEARCE_COLORS.primary;
  
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1.5,
        backgroundColor: alpha(bgColor, 0.05),
        border: `1px solid ${alpha(bgColor, 0.1)}`,
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: alpha(bgColor, 0.08),
          borderColor: alpha(bgColor, 0.2),
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Box sx={{ color: iconColor, fontSize: '1rem' }}>{icon}</Box>
        <Typography variant="caption" sx={{ color: SEARCE_COLORS.textSecondary, fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {label}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ 
        color: value ? SEARCE_COLORS.textPrimary : alpha(SEARCE_COLORS.textSecondary, 0.5), 
        fontSize: '0.875rem', 
        fontWeight: value ? 500 : 400,
        fontStyle: value ? 'normal' : 'italic',
        wordBreak: 'break-word', 
        pl: 2.5 
      }}>
        {value || '-'}
      </Typography>
    </Box>
  );
};

// Standard info row - for detailed sections
const InfoRow: React.FC<{ icon: React.ReactNode; label: string; value: string | null | undefined; variant?: 'standard' | 'compact' }> = ({ 
  icon, 
  label, 
  value, 
  variant = 'standard' 
}) => {
  if (!value) return null;
  
  if (variant === 'compact') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
        <Box sx={{ color: SEARCE_COLORS.primary, fontSize: '1rem', flexShrink: 0 }}>{icon}</Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" sx={{ display: 'block', color: SEARCE_COLORS.textSecondary, fontSize: '0.7rem', fontWeight: 500, mb: 0.25 }}>
            {label}
          </Typography>
          <Typography variant="body2" sx={{ color: SEARCE_COLORS.textPrimary, fontSize: '0.875rem', fontWeight: 500, wordBreak: 'break-word' }}>
            {value}
          </Typography>
        </Box>
      </Box>
    );
  }
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1 }}>
      <Box sx={{ 
        color: SEARCE_COLORS.primary, 
        mt: 0.5, 
        flexShrink: 0,
        p: 0.75,
        borderRadius: 1,
        backgroundColor: alpha(SEARCE_COLORS.primary, 0.08),
      }}>
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" sx={{ display: 'block', color: SEARCE_COLORS.textSecondary, fontSize: '0.75rem', fontWeight: 600, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ color: SEARCE_COLORS.textPrimary, fontSize: '0.875rem', wordBreak: 'break-word', lineHeight: 1.6 }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

// Section component with modern card styling
const Section: React.FC<{ 
  title: string; 
  children: React.ReactNode; 
  variant?: 'default' | 'elevated' | 'subtle';
  icon?: React.ReactNode;
}> = ({ title, children, variant = 'default', icon }) => {
  const getVariantStyles = () => {
    switch (variant) {
      case 'elevated':
        return {
          backgroundColor: '#ffffff',
          border: `1px solid ${SEARCE_COLORS.border}`,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        };
      case 'subtle':
        return {
          backgroundColor: alpha(SEARCE_COLORS.primary, 0.02),
          border: `1px solid ${alpha(SEARCE_COLORS.primary, 0.1)}`,
        };
      default:
        return {
          backgroundColor: '#ffffff',
          border: `1px solid ${SEARCE_COLORS.border}`,
        };
    }
  };
  
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 3,
        borderRadius: 2.5,
        ...getVariantStyles(),
        transition: 'all 0.2s ease',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, pb: 2, borderBottom: `2px solid ${alpha(SEARCE_COLORS.primary, 0.15)}` }}>
        {icon && (
          <Box sx={{ 
            p: 0.75, 
            borderRadius: 1.5, 
            backgroundColor: alpha(SEARCE_COLORS.primary, 0.1),
            color: SEARCE_COLORS.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {icon}
          </Box>
        )}
        <Typography 
          variant="subtitle1" 
          sx={{ 
            fontWeight: 700, 
            color: SEARCE_COLORS.textPrimary, 
            fontSize: '1rem',
            letterSpacing: '-0.2px',
          }}
        >
          {title}
        </Typography>
      </Box>
      {children}
    </Paper>
  );
};

// Text field display component - transparent, readable style
const TextFieldDisplay: React.FC<{ label: string; value: string | null | undefined; multiline?: boolean }> = ({ label, value, multiline = false }) => {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: SEARCE_COLORS.textPrimary, mb: 1, fontSize: '0.875rem' }}>
        {label}
      </Typography>
      <Box
        sx={{
          p: 2,
          backgroundColor: 'transparent',
          borderRadius: 1,
          border: 'none',
          minHeight: multiline ? '100px' : 'auto',
          fontSize: '0.875rem',
          lineHeight: '1.6',
          color: value ? SEARCE_COLORS.textPrimary : alpha(SEARCE_COLORS.textSecondary, 0.5),
          fontStyle: value ? 'normal' : 'italic',
          whiteSpace: multiline ? 'pre-wrap' : 'normal',
          wordBreak: 'break-word',
        }}
      >
        {value || '-'}
      </Box>
    </Box>
  );
};

export const CustomerRequestDetailModal: React.FC<CustomerRequestDetailModalProps> = ({
  open,
  onClose,
  request,
  loading = false,
}) => {
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewingFile, setViewingFile] = useState<{ path: string; name: string; type?: string; displayName?: string } | null>(null);

  if (!request) return null;

  // Handle file view
  const handleFileView = (filePath: string, fileName: string, fileType?: string, displayName?: string) => {
    setViewingFile({ path: filePath, name: fileName, type: fileType, displayName });
    setViewerOpen(true);
  };

  // Extract file path from GCS URL
  const extractFilePath = (url: string): string => {
    if (url.startsWith('gs://')) {
      const parts = url.replace('gs://', '').split('/');
      return parts.slice(1).join('/'); // Remove bucket name
    }
    if (url.includes('storage.googleapis.com')) {
      const match = url.match(/\/o\/(.+?)(?:\?|$)/);
      return match ? decodeURIComponent(match[1]) : url;
    }
    return url;
  };

  const getRequestTypeLabel = () => {
    switch (request.request_type) {
      case 'feedback':
        return 'Feedback Request';
      case 'escalation':
        return 'Escalation Request';
      case 'appreciation':
        return 'Appreciation Request';
      default:
        return 'Customer Request';
    }
  };

  const getStatusLabel = () => {
    if (request.request_type === 'escalation') {
      return getEscalationResolutionStatusLabel(request);
    }
    if (request.request_type === 'appreciation') {
      return 'Acknowledged';
    }
    // Feedback
    const statusMap: Record<string, string> = {
      'pending': 'Pending',
      'email_sent': 'Email Sent',
      'email_failed': 'Email Failed',
      'responded': 'Responded',
      'closed': 'Closed',
      'draft': 'Draft',
    };
    return statusMap[request.status.toLowerCase()] || request.status;
  };

  const latestFeedbackResponse = request.feedback_responses?.[0];

  const getTypeLabel = () => {
    if (request.request_type === 'feedback') {
      if (latestFeedbackResponse?.overall_satisfaction) {
        const satisfaction = latestFeedbackResponse.overall_satisfaction.toLowerCase();
        // Check for negative FIRST (since "dissatisfied" contains "satisfied")
        if (satisfaction.includes('highly dissatisfied') || satisfaction.includes('dissatisfied')) {
          return 'Negative';
        }
        // Then check for positive
        if (satisfaction.includes('highly satisfied') || satisfaction.includes('satisfied')) {
          return 'Positive';
        }
        // Neutral is a separate state
        if (satisfaction === 'neutral') {
          return 'Neutral';
        }
        // Default fallback
        return 'Neutral';
      }
      return request.status === 'responded' ? 'Pending Review' : 'Awaiting Response';
    }
    if (request.request_type === 'escalation' || request.request_type === 'appreciation') {
      const level = request.escalation_detail?.level || request.appreciation_detail?.level;
      return level === 'project' ? 'Project' : 'Individual';
    }
    return '-';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
          maxHeight: '90vh',
          height: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: `2px solid ${SEARCE_COLORS.primary}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: SEARCE_COLORS.textPrimary }}>
          {getRequestTypeLabel()}
        </Typography>
        <Button
          onClick={onClose}
          sx={{ minWidth: 'auto', p: 0.5, color: SEARCE_COLORS.textSecondary }}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ p: 3, flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <InlineSpinner size={40} centered centerMinHeight={160} />
          </Box>
        ) : (
          <Box>
            {/* Header Section */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: SEARCE_COLORS.textPrimary, mr: 2 }}>
                  {request.request_number}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  <StatusChip status={getTypeLabel()} type="type" />
                  <StatusChip status={getStatusLabel()} type="status" />
                </Box>
              </Box>

              {/* Submitter Info - Modern Card Grid */}
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  mb: 3,
                  borderRadius: 2.5,
                  background: `linear-gradient(135deg, ${alpha(SEARCE_COLORS.primary, 0.05)} 0%, ${alpha(SEARCE_COLORS.primary, 0.02)} 100%)`,
                  border: `1px solid ${alpha(SEARCE_COLORS.primary, 0.15)}`,
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <CompactInfo
                      icon={<PersonIcon sx={{ fontSize: 18 }} />}
                      label="Submitter"
                      value={request.submitter_name}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <CompactInfo
                      icon={<EmailIcon sx={{ fontSize: 18 }} />}
                      label="Email"
                      value={request.submitter_email}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <CompactInfo
                      icon={<BusinessIcon sx={{ fontSize: 18 }} />}
                      label="Business Unit"
                      value={request.business_unit}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Box>

            {/* Main Content - Two Column Layout */}
            <Grid container spacing={3}>
              {/* Left Column - Main Details */}
              <Grid item xs={12} md={8}>
                {/* Project Context */}
                <Section 
                  title="Project Context" 
                  variant="elevated"
                  icon={<AssignmentIcon sx={{ fontSize: 18 }} />}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <CompactInfo
                        icon={<AssignmentIcon sx={{ fontSize: 18 }} />}
                        label="Project Code"
                        value={request.project_code}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CompactInfo
                        icon={<AssignmentIcon sx={{ fontSize: 18 }} />}
                        label="Project Name"
                        value={request.project_name}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CompactInfo
                        icon={<BusinessIcon sx={{ fontSize: 18 }} />}
                        label="Account"
                        value={request.account_name}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CompactInfo
                        icon={<PersonIcon sx={{ fontSize: 18 }} />}
                        label="Project Owner"
                        value={request.project_owner}
                      />
                    </Grid>
                    {request.opportunity && (
                      <Grid item xs={12}>
                        <CompactInfo
                          icon={<AssignmentIcon sx={{ fontSize: 18 }} />}
                          label="Opportunity"
                          value={request.opportunity}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Section>

                {/* Type-Specific Details */}
                {request.request_type === 'feedback' && (
                  <>
                    <Section 
                      title="Feedback Details" 
                      variant="subtle"
                      icon={<EmailIcon sx={{ fontSize: 18 }} />}
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <CompactInfo
                            icon={<PersonIcon sx={{ fontSize: 18 }} />}
                            label="Customer Contact"
                            value={request.feedback_detail?.customer_contact_name}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <CompactInfo
                            icon={<AssignmentIcon sx={{ fontSize: 18 }} />}
                            label="Project Title"
                            value={request.feedback_detail?.project_title}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <CompactInfo
                            icon={<EmailIcon sx={{ fontSize: 18 }} />}
                            label="To Emails"
                            value={request.feedback_detail?.to_emails}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <CompactInfo
                            icon={<EmailIcon sx={{ fontSize: 18 }} />}
                            label="CC Emails"
                            value={request.feedback_detail?.cc_emails}
                          />
                        </Grid>
                      </Grid>
                      {request.feedback_detail?.email_error && (
                        <Box 
                          sx={{ 
                            mt: 2, 
                            p: 2, 
                            backgroundColor: alpha(SEARCE_COLORS.error, 0.1), 
                            borderRadius: 1.5, 
                            border: `1px solid ${alpha(SEARCE_COLORS.error, 0.3)}`,
                            borderLeft: `4px solid ${SEARCE_COLORS.error}`,
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: SEARCE_COLORS.error, mb: 1, fontSize: '0.875rem' }}>
                            Email Error
                          </Typography>
                          <Typography variant="body2" sx={{ color: SEARCE_COLORS.textPrimary, fontSize: '0.875rem' }}>
                            {request.feedback_detail.email_error}
                          </Typography>
                        </Box>
                      )}
                    </Section>
                  </>
                )}

                {request.request_type === 'escalation' && request.escalation_detail && (
                  <Section 
                    title="Escalation Details" 
                    variant="elevated"
                    icon={<BusinessIcon sx={{ fontSize: 18 }} />}
                  >
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      {request.escalation_detail.escalated_by_name && (
                        <Grid item xs={12} sm={6}>
                          <CompactInfo
                            icon={<PersonIcon sx={{ fontSize: 18 }} />}
                            label="Escalated By"
                            value={request.escalation_detail.escalated_by_name}
                          />
                        </Grid>
                      )}
                      {request.escalation_detail.escalated_by_designation && (
                        <Grid item xs={12} sm={6}>
                          <CompactInfo
                            icon={<PersonIcon sx={{ fontSize: 18 }} />}
                            label="Designation"
                            value={request.escalation_detail.escalated_by_designation}
                          />
                        </Grid>
                      )}
                      {request.escalation_detail.project_member_name && (
                        <Grid item xs={12} sm={6}>
                          <CompactInfo
                            icon={<PersonIcon sx={{ fontSize: 18 }} />}
                            label="Project Member"
                            value={request.escalation_detail.project_member_name}
                          />
                        </Grid>
                      )}
                      {request.escalation_detail.area_of_escalation && (
                        <Grid item xs={12} sm={6}>
                          <CompactInfo
                            icon={<BusinessIcon sx={{ fontSize: 18 }} />}
                            label="Area of Escalation"
                            value={request.escalation_detail.area_of_escalation}
                          />
                        </Grid>
                      )}
                      <Grid item xs={12} sm={6}>
                        <CompactInfo
                          icon={<CalendarIcon sx={{ fontSize: 18 }} />}
                          label="Date Raised"
                          value={request.escalation_detail.date_raised ? formatDateEnhanced(request.escalation_detail.date_raised) : null}
                        />
                      </Grid>
                      {request.escalation_detail.date_resolved && (
                        <Grid item xs={12} sm={6}>
                          <CompactInfo
                            icon={<CheckCircleIcon sx={{ fontSize: 18 }} />}
                            label="Date Resolved"
                            value={formatDateEnhanced(request.escalation_detail.date_resolved)}
                          />
                        </Grid>
                      )}
                    </Grid>
                    <TextFieldDisplay label="Issue Description" value={request.escalation_detail.issue_description} multiline />
                    {request.escalation_detail.solution_proposed && (
                      <TextFieldDisplay label="Solution Proposed" value={request.escalation_detail.solution_proposed} multiline />
                    )}
                    {request.escalation_detail.root_cause && (
                      <TextFieldDisplay label="Root Cause" value={request.escalation_detail.root_cause} multiline />
                    )}
                    {request.escalation_detail.learnings && (
                      <TextFieldDisplay label="Learnings" value={request.escalation_detail.learnings} multiline />
                    )}
                  </Section>
                )}

                {request.request_type === 'appreciation' && request.appreciation_detail && (
                  <Section 
                    title="Appreciation Details" 
                    variant="elevated"
                    icon={<CheckCircleIcon sx={{ fontSize: 18 }} />}
                  >
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      {request.appreciation_detail.customer_spoc_name && (
                        <Grid item xs={12} sm={6}>
                          <CompactInfo
                            icon={<PersonIcon sx={{ fontSize: 18 }} />}
                            label="Customer SPOC Name"
                            value={request.appreciation_detail.customer_spoc_name}
                          />
                        </Grid>
                      )}
                      {request.appreciation_detail.customer_spoc_designation && (
                        <Grid item xs={12} sm={6}>
                          <CompactInfo
                            icon={<PersonIcon sx={{ fontSize: 18 }} />}
                            label="Customer SPOC Designation"
                            value={request.appreciation_detail.customer_spoc_designation}
                          />
                        </Grid>
                      )}
                      {request.appreciation_detail.project_member_name && (
                        <Grid item xs={12} sm={6}>
                          <CompactInfo
                            icon={<PersonIcon sx={{ fontSize: 18 }} />}
                            label="Project Member"
                            value={request.appreciation_detail.project_member_name}
                          />
                        </Grid>
                      )}
                    </Grid>
                    <TextFieldDisplay label="Feedback Snippet" value={request.appreciation_detail.feedback_snippet} multiline />
                  </Section>
                )}
              </Grid>

              {/* Right Column - Files & Timeline */}
              <Grid item xs={12} md={4}>
                {/* Files & Links Card - Modern Design */}
                {(request.feedback_detail?.closure_report_url || 
                  request.appreciation_detail?.feedback_file_url) && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      mb: 3,
                      borderRadius: 2.5,
                      border: `1px solid ${SEARCE_COLORS.border}`,
                      backgroundColor: '#ffffff',
                      background: `linear-gradient(135deg, #ffffff 0%, ${alpha(SEARCE_COLORS.secondary, 0.02)} 100%)`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, pb: 2, borderBottom: `2px solid ${alpha(SEARCE_COLORS.secondary, 0.15)}` }}>
                      <Box sx={{ 
                        p: 0.75, 
                        borderRadius: 1.5, 
                        backgroundColor: alpha(SEARCE_COLORS.secondary, 0.1),
                        color: SEARCE_COLORS.secondary,
                      }}>
                        <VisibilityIcon sx={{ fontSize: 18 }} />
                      </Box>
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          fontWeight: 700, 
                          color: SEARCE_COLORS.textPrimary, 
                          fontSize: '1rem',
                          letterSpacing: '-0.2px',
                        }}
                      >
                        Files & Links
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {request.feedback_detail?.closure_report_url && (
                        <Button
                          fullWidth
                          variant="contained"
                          size="medium"
                          startIcon={<VisibilityIcon />}
                          onClick={() => {
                            const filePath = extractFilePath(request.feedback_detail!.closure_report_url!);
                            const fileName = filePath.split('/').pop() || 'closure-report.pdf';
                            const fileType = fileName.split('.').pop()?.toLowerCase();
                            const displayName = `Closure Report${request.project_name ? ` - ${request.project_name}` : ''}`;
                            handleFileView(request.feedback_detail!.closure_report_url!, fileName, fileType, displayName);
                          }}
                          sx={{
                            backgroundColor: SEARCE_COLORS.primary,
                            color: '#ffffff',
                            textTransform: 'none',
                            fontWeight: 600,
                            py: 1.25,
                            borderRadius: 1.5,
                            boxShadow: `0 2px 8px ${alpha(SEARCE_COLORS.primary, 0.3)}`,
                            '&:hover': {
                              backgroundColor: SEARCE_COLORS.primary,
                              boxShadow: `0 4px 12px ${alpha(SEARCE_COLORS.primary, 0.4)}`,
                              transform: 'translateY(-1px)',
                            },
                          }}
                        >
                          View Closure Report
                        </Button>
                      )}
                      {request.appreciation_detail?.feedback_file_url && (
                        <Button
                          fullWidth
                          variant="contained"
                          size="medium"
                          startIcon={<VisibilityIcon />}
                          onClick={() => {
                            const filePath = extractFilePath(request.appreciation_detail!.feedback_file_url!);
                            const fileName = filePath.split('/').pop() || 'feedback-file.pdf';
                            const fileType = fileName.split('.').pop()?.toLowerCase();
                            handleFileView(request.appreciation_detail!.feedback_file_url!, fileName, fileType);
                          }}
                          sx={{
                            backgroundColor: SEARCE_COLORS.primary,
                            color: '#ffffff',
                            textTransform: 'none',
                            fontWeight: 600,
                            py: 1.25,
                            borderRadius: 1.5,
                            boxShadow: `0 2px 8px ${alpha(SEARCE_COLORS.primary, 0.3)}`,
                            '&:hover': {
                              backgroundColor: SEARCE_COLORS.primary,
                              boxShadow: `0 4px 12px ${alpha(SEARCE_COLORS.primary, 0.4)}`,
                              transform: 'translateY(-1px)',
                            },
                          }}
                        >
                          View Feedback File
                        </Button>
                      )}
                    </Box>
                  </Paper>
                )}

                {/* Timeline Card - Modern Design */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    mb: 3,
                    borderRadius: 2.5,
                    border: `1px solid ${SEARCE_COLORS.border}`,
                    backgroundColor: '#ffffff',
                    background: `linear-gradient(135deg, #ffffff 0%, ${alpha(SEARCE_COLORS.info, 0.02)} 100%)`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, pb: 2, borderBottom: `2px solid ${alpha(SEARCE_COLORS.info, 0.15)}` }}>
                    <Box sx={{ 
                      p: 0.75, 
                      borderRadius: 1.5, 
                      backgroundColor: alpha(SEARCE_COLORS.info, 0.1),
                      color: SEARCE_COLORS.info,
                    }}>
                      <CalendarIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        fontWeight: 700, 
                        color: SEARCE_COLORS.textPrimary, 
                        fontSize: '1rem',
                        letterSpacing: '-0.2px',
                      }}
                    >
                      Timeline
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <Box
                      sx={{
                        position: 'relative',
                        pl: 3,
                        pb: 2.5,
                        borderLeft: `2px solid ${alpha(SEARCE_COLORS.primary, 0.2)}`,
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          left: -6,
                          top: 0,
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: SEARCE_COLORS.primary,
                          border: `2px solid #ffffff`,
                          boxShadow: `0 0 0 2px ${alpha(SEARCE_COLORS.primary, 0.2)}`,
                        }}
                      />
                      <Typography variant="caption" sx={{ display: 'block', color: SEARCE_COLORS.textSecondary, fontSize: '0.7rem', fontWeight: 600, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Created
                      </Typography>
                      <Typography variant="body2" sx={{ color: SEARCE_COLORS.textPrimary, fontSize: '0.875rem', fontWeight: 500 }}>
                        {formatFullDate(request.created_at)}
                      </Typography>
                    </Box>

                    {request.request_type === 'feedback' && request.feedback_detail?.email_sent_at && (
                      <Box
                        sx={{
                          position: 'relative',
                          pl: 3,
                          pb: 2.5,
                          borderLeft: `2px solid ${alpha(SEARCE_COLORS.secondary, 0.2)}`,
                        }}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            left: -6,
                            top: 0,
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: SEARCE_COLORS.secondary,
                            border: `2px solid #ffffff`,
                            boxShadow: `0 0 0 2px ${alpha(SEARCE_COLORS.secondary, 0.2)}`,
                          }}
                        />
                        <Typography variant="caption" sx={{ display: 'block', color: SEARCE_COLORS.textSecondary, fontSize: '0.7rem', fontWeight: 600, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Email Sent
                        </Typography>
                        <Typography variant="body2" sx={{ color: SEARCE_COLORS.textPrimary, fontSize: '0.875rem', fontWeight: 500 }}>
                          {formatFullDate(request.feedback_detail.email_sent_at)}
                        </Typography>
                      </Box>
                    )}

                    {request.request_type === 'feedback' && latestFeedbackResponse?.submitted_at && (
                      <Box
                        sx={{
                          position: 'relative',
                          pl: 3,
                          pb: 2.5,
                          borderLeft: `2px solid ${alpha(SEARCE_COLORS.success, 0.2)}`,
                        }}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            left: -6,
                            top: 0,
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: SEARCE_COLORS.success,
                            border: `2px solid #ffffff`,
                            boxShadow: `0 0 0 2px ${alpha(SEARCE_COLORS.success, 0.2)}`,
                          }}
                        />
                        <Typography variant="caption" sx={{ display: 'block', color: SEARCE_COLORS.textSecondary, fontSize: '0.7rem', fontWeight: 600, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Response Submitted
                        </Typography>
                        <Typography variant="body2" sx={{ color: SEARCE_COLORS.textPrimary, fontSize: '0.875rem', fontWeight: 500 }}>
                          {formatFullDate(latestFeedbackResponse.submitted_at)}
                        </Typography>
                      </Box>
                    )}

                    {request.request_type === 'escalation' && request.escalation_detail && (
                      <>
                        <Box
                          sx={{
                            position: 'relative',
                            pl: 3,
                            pb: 2.5,
                            borderLeft: `2px solid ${alpha(SEARCE_COLORS.warning, 0.2)}`,
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              left: -6,
                              top: 0,
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: SEARCE_COLORS.warning,
                              border: `2px solid #ffffff`,
                              boxShadow: `0 0 0 2px ${alpha(SEARCE_COLORS.warning, 0.2)}`,
                            }}
                          />
                          <Typography variant="caption" sx={{ display: 'block', color: SEARCE_COLORS.textSecondary, fontSize: '0.7rem', fontWeight: 600, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Date Raised
                          </Typography>
                          <Typography variant="body2" sx={{ color: SEARCE_COLORS.textPrimary, fontSize: '0.875rem', fontWeight: 500 }}>
                            {formatFullDate(request.escalation_detail.date_raised)}
                          </Typography>
                        </Box>
                        {request.escalation_detail.date_resolved && (
                          <Box
                            sx={{
                              position: 'relative',
                              pl: 3,
                              pb: 2.5,
                              borderLeft: `2px solid ${alpha(SEARCE_COLORS.success, 0.2)}`,
                            }}
                          >
                            <Box
                              sx={{
                                position: 'absolute',
                                left: -6,
                                top: 0,
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: SEARCE_COLORS.success,
                                border: `2px solid #ffffff`,
                                boxShadow: `0 0 0 2px ${alpha(SEARCE_COLORS.success, 0.2)}`,
                              }}
                            />
                            <Typography variant="caption" sx={{ display: 'block', color: SEARCE_COLORS.textSecondary, fontSize: '0.7rem', fontWeight: 600, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Date Resolved
                            </Typography>
                            <Typography variant="body2" sx={{ color: SEARCE_COLORS.success, fontSize: '0.875rem', fontWeight: 600 }}>
                              {formatFullDate(request.escalation_detail.date_resolved)}
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}

                    {request.updated_at && request.updated_at !== request.created_at && (
                      <Box
                        sx={{
                          position: 'relative',
                          pl: 3,
                          borderLeft: `2px solid ${alpha(SEARCE_COLORS.info, 0.2)}`,
                        }}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            left: -6,
                            top: 0,
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: SEARCE_COLORS.info,
                            border: `2px solid #ffffff`,
                            boxShadow: `0 0 0 2px ${alpha(SEARCE_COLORS.info, 0.2)}`,
                          }}
                        />
                        <Typography variant="caption" sx={{ display: 'block', color: SEARCE_COLORS.textSecondary, fontSize: '0.7rem', fontWeight: 600, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Last Updated
                        </Typography>
                        <Typography variant="body2" sx={{ color: SEARCE_COLORS.textPrimary, fontSize: '0.875rem', fontWeight: 500 }}>
                          {formatFullDate(request.updated_at)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Customer Responses Table - Full Width */}
            {request.request_type === 'feedback' && request.feedback_responses && request.feedback_responses.length > 0 && (
              <Box sx={{ mt: 4, width: '100%' }}>
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{
                    border: `1px solid ${SEARCE_COLORS.border}`,
                    borderRadius: 2,
                    overflowX: 'auto',
                    overflowY: 'auto',
                    width: '100%',
                    maxHeight: '400px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  }}
                >
                  <Table sx={{ width: '100%', tableLayout: 'auto' }}>
                            <TableHead>
                              <TableRow>
                                <TableCell
                                  align="center"
                                  sx={{
                                    backgroundColor: SEARCE_COLORS.primary,
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontSize: '0.8rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    borderRight: `1px solid ${alpha('#fff', 0.2)}`,
                                    padding: '10px 12px',
                                    minWidth: '140px',
                                  }}
                                >
                                  <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Customer Name
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    backgroundColor: SEARCE_COLORS.primary,
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontSize: '0.8rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    borderRight: `1px solid ${alpha('#fff', 0.2)}`,
                                    padding: '10px 12px',
                                    minWidth: '180px',
                                  }}
                                >
                                  <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Customer Email
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    backgroundColor: SEARCE_COLORS.primary,
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontSize: '0.8rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    borderRight: `1px solid ${alpha('#fff', 0.2)}`,
                                    padding: '10px 12px',
                                    minWidth: '150px',
                                  }}
                                >
                                  <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Account Name
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    backgroundColor: SEARCE_COLORS.primary,
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontSize: '0.8rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    borderRight: `1px solid ${alpha('#fff', 0.2)}`,
                                    padding: '10px 12px',
                                    minWidth: '160px',
                                  }}
                                >
                                  <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Overall Satisfaction
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    backgroundColor: SEARCE_COLORS.primary,
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontSize: '0.8rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    borderRight: `1px solid ${alpha('#fff', 0.2)}`,
                                    padding: '10px 12px',
                                    minWidth: '120px',
                                  }}
                                >
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                    <Box sx={{ fontSize: '0.65rem', opacity: 0.85, fontWeight: 500, lineHeight: 1.2, mb: 0.3 }}>
                                      Delivery
                                    </Box>
                                    <Box sx={{ fontSize: '0.8rem', fontWeight: 700, lineHeight: 1.2 }}>
                                      Completion
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    backgroundColor: SEARCE_COLORS.primary,
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontSize: '0.8rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    borderRight: `1px solid ${alpha('#fff', 0.2)}`,
                                    padding: '10px 12px',
                                    minWidth: '120px',
                                  }}
                                >
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                    <Box sx={{ fontSize: '0.65rem', opacity: 0.85, fontWeight: 500, lineHeight: 1.2, mb: 0.3 }}>
                                      Delivery
                                    </Box>
                                    <Box sx={{ fontSize: '0.8rem', fontWeight: 700, lineHeight: 1.2 }}>
                                      Timeline
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    backgroundColor: SEARCE_COLORS.primary,
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontSize: '0.8rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    borderRight: `1px solid ${alpha('#fff', 0.2)}`,
                                    padding: '10px 12px',
                                    minWidth: '140px',
                                  }}
                                >
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                    <Box sx={{ fontSize: '0.65rem', opacity: 0.85, fontWeight: 500, lineHeight: 1.2, mb: 0.3 }}>
                                      Delivery
                                    </Box>
                                    <Box sx={{ fontSize: '0.8rem', fontWeight: 700, lineHeight: 1.2 }}>
                                      Closure & Handover
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    backgroundColor: SEARCE_COLORS.primary,
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontSize: '0.8rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    borderRight: `1px solid ${alpha('#fff', 0.2)}`,
                                    padding: '10px 12px',
                                    minWidth: '140px',
                                  }}
                                >
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                    <Box sx={{ fontSize: '0.65rem', opacity: 0.85, fontWeight: 500, lineHeight: 1.2, mb: 0.3 }}>
                                      Team
                                    </Box>
                                    <Box sx={{ fontSize: '0.8rem', fontWeight: 700, lineHeight: 1.2 }}>
                                      Technical Expertise
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    backgroundColor: SEARCE_COLORS.primary,
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontSize: '0.8rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    borderRight: `1px solid ${alpha('#fff', 0.2)}`,
                                    padding: '10px 12px',
                                    minWidth: '130px',
                                  }}
                                >
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                    <Box sx={{ fontSize: '0.65rem', opacity: 0.85, fontWeight: 500, lineHeight: 1.2, mb: 0.3 }}>
                                      Team
                                    </Box>
                                    <Box sx={{ fontSize: '0.8rem', fontWeight: 700, lineHeight: 1.2 }}>
                                      Support Provided
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    backgroundColor: SEARCE_COLORS.primary,
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontSize: '0.8rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    borderRight: `1px solid ${alpha('#fff', 0.2)}`,
                                    padding: '10px 12px',
                                    minWidth: '130px',
                                  }}
                                >
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                    <Box sx={{ fontSize: '0.65rem', opacity: 0.85, fontWeight: 500, lineHeight: 1.2, mb: 0.3 }}>
                                      Team
                                    </Box>
                                    <Box sx={{ fontSize: '0.8rem', fontWeight: 700, lineHeight: 1.2 }}>
                                      Responsiveness
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    backgroundColor: SEARCE_COLORS.primary,
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontSize: '0.8rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    borderRight: `1px solid ${alpha('#fff', 0.2)}`,
                                    padding: '10px 12px',
                                    minWidth: '120px',
                                  }}
                                >
                                  <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Response Timestamp
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    backgroundColor: SEARCE_COLORS.primary,
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontSize: '0.8rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    borderRight: `1px solid ${alpha('#fff', 0.2)}`,
                                    padding: '10px 12px',
                                    width: '380px',
                                    minWidth: '380px',
                                    maxWidth: '380px',
                                  }}
                                >
                                  <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Specific Contributions
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    backgroundColor: SEARCE_COLORS.primary,
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontSize: '0.8rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    padding: '10px 12px',
                                    width: '380px',
                                    minWidth: '380px',
                                    maxWidth: '380px',
                                  }}
                                >
                                  <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Areas for Improvement
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    backgroundColor: SEARCE_COLORS.primary,
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontSize: '0.8rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    padding: '10px 12px',
                                    minWidth: '100px',
                                  }}
                                >
                                  <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Zoho Issue
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {request.feedback_responses.map((response, index) => (
                                <TableRow
                                  key={`${response.customer_email || 'response'}-${response.submitted_at || index}`}
                                  sx={{
                                    '&:nth-of-type(even)': {
                                      backgroundColor: alpha(SEARCE_COLORS.primary, 0.02),
                                    },
                                            '&:hover': {
                                      backgroundColor: alpha(SEARCE_COLORS.primary, 0.05),
                                    },
                                  }}
                                >
                                  <TableCell
                                    sx={{
                                      borderRight: `1px solid ${SEARCE_COLORS.border}`,
                                      fontSize: '0.875rem',
                                      padding: '8px 12px',
                                      lineHeight: 1.5,
                                    }}
                                  >
                                    {response.customer_name || '-'}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      borderRight: `1px solid ${SEARCE_COLORS.border}`,
                                      fontSize: '0.875rem',
                                      padding: '8px 12px',
                                      lineHeight: 1.5,
                                    }}
                                  >
                                    {response.customer_email || '-'}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      borderRight: `1px solid ${SEARCE_COLORS.border}`,
                                      fontSize: '0.875rem',
                                      padding: '8px 12px',
                                      lineHeight: 1.5,
                                    }}
                                  >
                                    {response.account_name || '-'}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      borderRight: `1px solid ${SEARCE_COLORS.border}`,
                                      padding: '8px 12px',
                                    }}
                                  >
                                    {response.overall_satisfaction ? (
                                      <StatusChip status={response.overall_satisfaction} />
                                    ) : (
                                      <Typography variant="body2" sx={{ color: alpha(SEARCE_COLORS.textSecondary, 0.5), fontSize: '0.875rem' }}>
                                        -
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{
                                      borderRight: `1px solid ${SEARCE_COLORS.border}`,
                                      fontSize: '0.875rem',
                                      fontWeight: 500,
                                      padding: '8px 12px',
                                      lineHeight: 1.5,
                                    }}
                                  >
                                    {response.delivery_completion_of_deliverables || '-'}
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{
                                      borderRight: `1px solid ${SEARCE_COLORS.border}`,
                                      fontSize: '0.875rem',
                                      fontWeight: 500,
                                      padding: '8px 12px',
                                      lineHeight: 1.5,
                                    }}
                                  >
                                    {response.delivery_adherence_to_timeline || '-'}
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{
                                      borderRight: `1px solid ${SEARCE_COLORS.border}`,
                                      fontSize: '0.875rem',
                                      fontWeight: 500,
                                      padding: '8px 12px',
                                      lineHeight: 1.5,
                                    }}
                                  >
                                    {response.delivery_project_closure_handover || '-'}
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{
                                      borderRight: `1px solid ${SEARCE_COLORS.border}`,
                                      fontSize: '0.875rem',
                                      fontWeight: 500,
                                      padding: '8px 12px',
                                      lineHeight: 1.5,
                                    }}
                                  >
                                    {response.team_technical_expertise || '-'}
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{
                                      borderRight: `1px solid ${SEARCE_COLORS.border}`,
                                      fontSize: '0.875rem',
                                      fontWeight: 500,
                                      padding: '8px 12px',
                                      lineHeight: 1.5,
                                    }}
                                  >
                                    {response.team_support_provided || '-'}
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{
                                      borderRight: `1px solid ${SEARCE_COLORS.border}`,
                                      fontSize: '0.875rem',
                                      fontWeight: 500,
                                      padding: '8px 12px',
                                      lineHeight: 1.5,
                                    }}
                                  >
                                    {response.team_responsiveness || '-'}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      borderRight: `1px solid ${SEARCE_COLORS.border}`,
                                      fontSize: '0.875rem',
                                      padding: '8px 12px',
                                      lineHeight: 1.5,
                                    }}
                                  >
                                    {response.submitted_at ? formatDateDDMonYYYY(response.submitted_at) : '-'}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      borderRight: `1px solid ${SEARCE_COLORS.border}`,
                                      fontSize: '0.875rem',
                                      padding: '8px 12px',
                                      lineHeight: 1.5,
                                      width: '420px',
                                      minWidth: '420px',
                                      maxWidth: '420px',
                                      verticalAlign: 'top',
                                    }}
                                  >
                                    {response.specific_contributions ? (
                                      <Typography
                                        variant="body2"
                                        component="span"
                                        sx={{
                                          fontSize: '0.875rem',
                                          whiteSpace: 'pre-wrap',
                                          wordBreak: 'break-word',
                                          overflowWrap: 'break-word',
                                          display: 'block',
                                        }}
                                      >
                                        {response.specific_contributions}
                                      </Typography>
                                    ) : (
                                      <Typography variant="body2" sx={{ color: alpha(SEARCE_COLORS.textSecondary, 0.5), fontSize: '0.875rem' }}>
                                        -
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontSize: '0.875rem',
                                      padding: '8px 12px',
                                      lineHeight: 1.5,
                                      width: '420px',
                                      minWidth: '420px',
                                      maxWidth: '420px',
                                      verticalAlign: 'top',
                                    }}
                                  >
                                    {response.areas_for_improvement ? (
                                      <Typography
                                        variant="body2"
                                        component="span"
                                        sx={{
                                          fontSize: '0.875rem',
                                          whiteSpace: 'pre-wrap',
                                          wordBreak: 'break-word',
                                          overflowWrap: 'break-word',
                                          display: 'block',
                                        }}
                                      >
                                        {response.areas_for_improvement}
                                      </Typography>
                                    ) : (
                                      <Typography variant="body2" sx={{ color: alpha(SEARCE_COLORS.textSecondary, 0.5), fontSize: '0.875rem' }}>
                                        -
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{
                                      borderRight: `1px solid ${SEARCE_COLORS.border}`,
                                      fontSize: '0.875rem',
                                      padding: '8px 12px',
                                      lineHeight: 1.5,
                                    }}
                                  >
                                    {response.zoho_issue_link && response.zoho_status && response.zoho_status !== 'N/A' && !response.zoho_status.includes('Failed') ? (
                                      <Link
                                        {...zohoIssueLinkProps}
                                        href={normalizeZohoIssueLinkUrl(response.zoho_issue_link)}
                                        sx={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: 0.5,
                                          color: SEARCE_COLORS.primary,
                                          textDecoration: 'none',
                                          fontWeight: 500,
                                          fontSize: '0.875rem',
                                          '&:hover': {
                                            textDecoration: 'underline',
                                          },
                                        }}
                                      >
                                        View in Zoho
                                        <LaunchIcon sx={{ fontSize: 14 }} />
                                      </Link>
                                    ) : response.zoho_status ? (
                                      <StatusChip status={response.zoho_status} type="status" />
                                    ) : (
                                      <Typography variant="body2" sx={{ color: alpha(SEARCE_COLORS.textSecondary, 0.5), fontSize: '0.875rem' }}>
                                        -
                                      </Typography>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2, borderTop: `1px solid ${SEARCE_COLORS.border}` }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            color: SEARCE_COLORS.primary,
            borderColor: SEARCE_COLORS.primary,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: alpha(SEARCE_COLORS.primary, 0.1),
              borderColor: SEARCE_COLORS.primary,
            },
          }}
        >
          Close
        </Button>
      </DialogActions>

      {/* File Viewer Modal */}
      {viewingFile && (
        <FileViewer
          open={viewerOpen}
          onClose={() => {
            setViewerOpen(false);
            setViewingFile(null);
          }}
          filePath={viewingFile.path}
          fileName={viewingFile.name}
          fileType={viewingFile.type}
          displayName={viewingFile.displayName}
        />
      )}
    </Dialog>
  );
};

